import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import CardBase from "@/components/cards/CardBase";
import TMSTableCell from "./TMSTableCell";
import { getSelectedGame } from "@/lib/stores/unifiedUserStore";
import {
  getLanguageFlag,
  getLanguageName,
  getSourceTextLabel,
} from "@/lib/dataSources";
import {
  StringsItem,
  deleteString,
} from "@/lib/stores/stringsStore";
import { useStringsDataContext } from "@/providers/StringsDataProvider";
import UnifiedTableFilter, { FilterValue, FilterConfig } from "../TableFilter";
import { ColumnVisibility } from "./ColumnVisibilityDropdown";
import TranslationWarningDialog from "../TranslationWarningDialog";
import ExpandableCellEditor from "./ExpandableCellEditor";
import { useTMSStringUpdates } from "@/hooks/useTMSStringUpdates";
import { useZoom } from "@/providers/ZoomProvider";
import { getWorkflowStatuses } from "@/lib/utils/statusColors";
import { useStatusData } from "@/hooks/useStatusData";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TMSTableWidgetProps {
  showTitle?: boolean;
  title?: string;
  backgroundColor?: string;
  shadowType?: "black" | "gradient" | "none" | "color";
  shadowColor?: string;
  visibleLanguages?: number; // Number of languages to show initially
}

// Constants for table configuration
const VISIBLE_LANGUAGES = 5;
const MAX_COLUMN_WIDTH = 350; // Increased maximum width to allow more expansion
const MIN_COLUMN_WIDTH = 180; // Minimum width for language columns

// Column definitions for the fixed left side - will be created dynamically in component

const TMSTableWidget: React.FC<TMSTableWidgetProps> = ({
  showTitle = false,
  title = "TMS Table",
  backgroundColor = "transparent",
  shadowType = "black",
  shadowColor = "#000000",
  visibleLanguages = VISIBLE_LANGUAGES, // Default to showing 5 languages
}) => {
  const { t } = usePlatformTranslation();
  const { zoom } = useZoom();
  
  // Load status data for filters
  useStatusData();
  
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([]);
  
  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    checkbox: true,
    abbrev: true,
    status: true,
    limit: false,
    context: true,
    sourceText: true,
  });

  // Window resize state for column width recalculation - account for platform zoom
  const [windowWidth, setWindowWidth] = useState(window.innerWidth / zoom);

  // Translation warning dialog state
  const [translationWarning, setTranslationWarning] = useState<{
    open: boolean;
    rowData: any;
    columnKey: string;
    newValue: string;
    translationCount: number;
  } | null>(null);

  // Get the string updates hook
  const { updateStringField } = useTMSStringUpdates();

  // Delete confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    selectedIds: string[];
    count: number;
  } | null>(null);

  // Expandable cell editor state
  const [expandableEditor, setExpandableEditor] = useState<{
    isVisible: boolean;
    cellRect: DOMRect | null;
    value: string;
    cellWidth: number;
    maxWidth: number;
    rowData: any;
    columnKey: string;
  } | null>(null);

  // Refs for scroll synchronization between fixed and scrollable sections
  const fixedScrollRef = useRef<HTMLDivElement>(null);
  const scrollableScrollRef = useRef<HTMLDivElement>(null);

  // Get selected game data directly to avoid hook re-renders
  const selectedGame = getSelectedGame();
  const gameId = selectedGame?.id;

  // Use StringsDataProvider for consistent data management
  const { stringsItems, isLoading } = useStringsDataContext();
  

  // Debounced update mechanism to prevent rapid successive updates
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<Map<string, { rowData: any; columnKey: string; newValue: string }>>(new Map());

  // Use provider data for display
  const displayStringsItems = stringsItems;

  // Filter handlers - use useCallback to prevent unnecessary re-renders
  const handleFilter = useCallback(() => {
    console.log("Filter clicked");
    // TODO: Implement filter functionality
  }, []);

  const handleExport = useCallback(() => {
    console.log("Export clicked");
    // TODO: Implement export functionality
  }, []);

  const handleComments = useCallback(() => {
    console.log("Comments clicked");
    // TODO: Implement comments functionality
  }, []);

  // Transform game language codes to display format with flags and names
  const supportedLanguages = useMemo(() => {
    // Get supported languages from the selected game settings
    const gameLanguages = selectedGame?.settings?.languages || [];
    
    // If no game languages configured, use default fallback languages
    if (gameLanguages.length === 0) {
      console.warn(
        "No languages configured in game settings, using fallback languages"
      );
      return [];
    }

    // Helper function to extract code from various formats
    const extractCode = (langCode: any): string => {
      if (typeof langCode === 'string') {
        return langCode;
      }
      if (langCode && typeof langCode === 'object') {
        if (typeof langCode.code === 'string') {
          return langCode.code;
        }
        if (langCode.code && typeof langCode.code === 'object' && typeof langCode.code.code === 'string') {
          return langCode.code.code;
        }
      }
      console.warn('Could not extract language code from:', langCode);
      return '';
    };

    // Filter out the default language since it's already in the source text column
    const defaultLanguage = selectedGame?.settings?.defaultLanguage;
    
    const filteredLanguages = gameLanguages.filter((langCode) => {
      const code = extractCode(langCode);
      return code !== defaultLanguage && code !== '';
    });

    const mappedLanguages = filteredLanguages.map((langCode) => {
      const code = extractCode(langCode);
      const name = getLanguageName(code);
      const flag = getLanguageFlag(code);
      
      return {
        code: code, // Store the extracted string code instead of the original object
        originalCode: langCode, // Keep original for reference if needed
        name: name,
        flag: flag,
      };
    });

    console.log('🌍 Filtered languages (excluding default):', {
      defaultLanguage,
      allLanguages: gameLanguages.length,
      filteredCount: mappedLanguages.length,
      languages: mappedLanguages.map(l => ({ code: l.code, name: l.name, flag: l.flag }))
    });

    return mappedLanguages;
  }, [selectedGame?.settings?.languages, selectedGame?.settings?.defaultLanguage]);

  // Available filter configurations (must be after supportedLanguages is defined)
  const filterConfigs: FilterConfig[] = useMemo(() => {
    // Get the loca (TMS) workflow statuses from the status management system
    const locaStatuses = getWorkflowStatuses('loca', gameId);
    
    return [
      {
        type: 'language',
        label: 'Language',
        options: supportedLanguages.map(lang => ({
          value: lang.code,
          label: lang.name,
          flag: lang.flag
        }))
      },
      {
        type: 'sourceStatus',
        label: 'Source Status',
        options: locaStatuses.length > 0 
          ? locaStatuses.map(s => ({
              value: s.status,
              label: s.status.charAt(0).toUpperCase() + s.status.slice(1) // Capitalize first letter
            }))
          : [
              // Fallback to default statuses if no workflow is configured
              { value: 'draft', label: 'Draft' },
              { value: 'review', label: 'Review' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'published', label: 'Published' },
              { value: 'implemented', label: 'Implemented' }
            ]
      }
    ];
  }, [supportedLanguages, gameId]);

  // Get visible languages and remaining languages for scrolling
  // If language filter is active, only show filtered languages
  const languageFilter = activeFilters.find(f => f.type === 'language');
  const filteredLanguages = languageFilter && languageFilter.values.length > 0
    ? supportedLanguages.filter(lang => languageFilter.values.includes(lang.code))
    : supportedLanguages;

  const visibleLanguagesList = filteredLanguages.slice(0, visibleLanguages);
  const remainingLanguages = filteredLanguages.slice(visibleLanguages);

  // Create source columns with dynamic label based on selected game's default language
  const allSourceColumns = useMemo(
    () => [
      { key: "checkbox", type: "checkbox", width: 40, label: "" },
      { key: "abbrev", type: "text", width: 150, label: "Loca Key" },
      { key: "status", type: "badge", width: 120, label: "Source Status" },
      { key: "limit", type: "text", width: 80, label: "Lim" },
      { key: "context", type: "text", width: 150, label: "Context" },
      {
        key: "sourceText",
        type: "text",
        width: 220,
        label: `Source (${getSourceTextLabel(
          selectedGame?.settings?.defaultLanguage
        )})`,
      },
    ],
    [selectedGame?.settings?.defaultLanguage]
  );

  // Filter source columns based on visibility settings
  const sourceColumns = useMemo(
    () => allSourceColumns.filter(col => columnVisibility[col.key as keyof ColumnVisibility]),
    [allSourceColumns, columnVisibility]
  );

  // Calculate flexible column widths for language columns
  const calculateLanguageColumnWidths = useMemo(() => {
    
    const totalSourceWidth = sourceColumns.reduce(
      (sum, col) => sum + col.width,
      0
    );
    const totalLanguageColumns =
      visibleLanguagesList.length + remainingLanguages.length;

    if (totalLanguageColumns === 0) return [];

    // Account for container padding and margins
    // CardBase has padding, filter bar, borders, scrollbars, etc.
    // Total approximate overhead: ~50-60px (padding + borders + scrollbar)
    const containerOverhead = 50;
    const availableContainerWidth = windowWidth - containerOverhead;
    
    // Calculate available width for language columns
    const availableLanguageWidth = availableContainerWidth - totalSourceWidth -47;

    // Distribute available space evenly among language columns
    // Ensure minimum width is respected
    const baseWidth = Math.max(
      MIN_COLUMN_WIDTH,
      availableLanguageWidth / totalLanguageColumns
    );

    // Create array of widths for all language columns
    const widths = [];
    for (let i = 0; i < totalLanguageColumns; i++) {
      widths.push(baseWidth);
    }
    return widths;
  }, [
    sourceColumns,
    visibleLanguagesList.length,
    remainingLanguages.length,
    windowWidth,
  ]);

  // Add window resize listener to recalculate column widths - account for platform zoom
  useEffect(() => {
    const handleResize = () => {
      // Divide by zoom to get the actual unzoomed width for calculations
      setWindowWidth(window.innerWidth / zoom);
    };

    // Initial update with current zoom
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [zoom]);

  // Create a stable key for forcing re-renders when translations change
  const translationsKey = useMemo(() => {
    return displayStringsItems.map(item => 
      `${item.id}:${item.translations?.length || 0}:${item.translations?.map(t => t.id).join(',') || ''}`
    ).join('|');
  }, [displayStringsItems]);

  // Transform strings items to display format with search filtering
  const displayData = useMemo(() => {
    let filteredItems = displayStringsItems;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredItems = filteredItems.filter(
        (item) =>
          item.abbrev?.toLowerCase().includes(searchLower) ||
          item.source_text?.toLowerCase().includes(searchLower) ||
          item.status?.toLowerCase().includes(searchLower) ||
          (item.translations || []).some((trans) =>
            trans?.translated_text?.toLowerCase().includes(searchLower)
          )
      );
    }

    // Apply source status filter
    const sourceStatusFilter = activeFilters.find(f => f.type === 'sourceStatus');
    if (sourceStatusFilter && sourceStatusFilter.values.length > 0) {
      filteredItems = filteredItems.filter(item => 
        sourceStatusFilter.values.includes(item.status || '')
      );
    }

    // Apply language filter (show only items that have translations in selected languages)
    const languageFilter = activeFilters.find(f => f.type === 'language');
    if (languageFilter && languageFilter.values.length > 0) {
      filteredItems = filteredItems.filter(item => {
        // Keep items that have at least one translation in the filtered languages
        // OR show all items if we're just using it as a view filter
        const hasFilteredLanguageTranslation = (item.translations || []).some(trans =>
          languageFilter.values.includes(trans.language_code)
        );
        // For now, show all items (don't filter out), just use language filter for column visibility
        return true;
      });
    }

    return filteredItems.map((item) => ({
      id: item.id,
      abbrev: item.abbrev,
      status: item.status,
      sourceText: item.source_text,
      limit: item.metadata?.length?.toString() || "0",
      context: item.context || "",
      // Keep original translations for editing, but also create display format
      originalTranslations: item.translations || [],
      translations: item.translations?.reduce((acc, trans) => {
        acc[trans.language_code] = {
          text: trans.translated_text,
          status: trans.approved ? "approved" : "translated",
        };
        return acc;
      }, {}) || {}, // Ensure we always have an object, even if no translations
      isEmpty: false,
    }));
  }, [displayStringsItems, searchTerm, activeFilters]);

  // Download handler - must be after displayData and language lists are computed
  const handleDownload = useCallback(() => {
    if (displayData.length === 0) {
      console.warn("No data to download");
      return;
    }

    // Get the languages to include in the CSV (filtered or all)
    const languagesToInclude = [...visibleLanguagesList, ...remainingLanguages];

    // Build CSV headers
    const headers = [
      'Loca Key',
      'Source Status',
      'Limit',
      `Source (${getSourceTextLabel(selectedGame?.settings?.defaultLanguage)})`,
      ...languagesToInclude.map(lang => `${lang.name} (${lang.code})`)
    ];

    // Build CSV rows
    const rows = displayData.map(item => {
      const row = [
        item.abbrev || '',
        item.status || '',
        item.limit || '0',
        item.sourceText || '',
        ...languagesToInclude.map(lang => {
          const translation = item.translations[lang.code];
          return translation?.text || '';
        })
      ];
      
      // Escape CSV values (handle quotes and commas)
      return row.map(value => {
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    });

    // Combine headers and rows
    const csvContent = [
      headers.map(header => {
        // Escape header values
        if (header.includes(',') || header.includes('"') || header.includes('\n')) {
          return `"${header.replace(/"/g, '""')}"`;
        }
        return header;
      }).join(','),
      ...rows
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with timestamp and game name
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const gameName = selectedGame?.name?.replace(/[^a-z0-9]/gi, '_') || 'translations';
    const filterSuffix = activeFilters.length > 0 ? '_filtered' : '';
    const filename = `${gameName}_export${filterSuffix}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Downloaded ${displayData.length} rows to ${filename}`);
  }, [displayData, visibleLanguagesList, remainingLanguages, selectedGame, activeFilters]);

  // Handle select all checkbox - use useCallback to prevent unnecessary re-renders
  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedRows(new Set(displayStringsItems.map((item) => item.id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [displayStringsItems]);

  // Handle individual row selection - use useCallback to prevent unnecessary re-renders
  const handleRowSelection = useCallback((id: string, checked: boolean) => {
    // Only allow selection of non-empty rows
    if (id.startsWith("empty-")) return;

    setSelectedRows(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      
      // Update select all state
      if (newSelected.size === displayStringsItems.length) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
      
      return newSelected;
    });
  }, [displayStringsItems]);

  // Debounced update mechanism to prevent rapid successive updates
  const debouncedUpdate = useCallback((rowData: any, columnKey: string, newValue: string) => {
    const updateKey = `${rowData.id}-${columnKey}`;
    
    console.log('⏰ TMSTableWidget: debouncedUpdate called', {
      updateKey,
      rowId: rowData?.id,
      columnKey,
      newValue,
      isEmpty: !newValue || newValue.trim() === '',
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 3)
    });
    
    // Store the pending update
    pendingUpdatesRef.current.set(updateKey, { rowData, columnKey, newValue });
    
    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Set new timeout for debounced update
    updateTimeoutRef.current = setTimeout(async () => {
      const updates = Array.from(pendingUpdatesRef.current.values());
      pendingUpdatesRef.current.clear();
      
      
      // Process all pending updates
      console.log('🔄 TMSTableWidget: Processing debounced updates', {
        updatesCount: updates.length,
        updates: updates.map(u => ({
          rowId: u.rowData.id,
          columnKey: u.columnKey,
          newValue: u.newValue,
          isEmpty: !u.newValue || u.newValue.trim() === ''
        })),
        timestamp: new Date().toISOString()
      });

      for (const update of updates) {
        // Skip empty translation updates
        if (update.columnKey.includes('translations.') && 
            update.columnKey.includes('.translated_text') && 
            (!update.newValue || update.newValue.trim() === '')) {
          console.log('🚫 TMSTableWidget: Skipping empty translation update', {
            rowId: update.rowData.id,
            columnKey: update.columnKey,
            newValue: update.newValue
          });
          continue;
        }
        
        console.log('➡️ TMSTableWidget: Processing update', {
          rowId: update.rowData.id,
          columnKey: update.columnKey,
          newValue: update.newValue
        });
        
        await updateStringField(update.rowData, update.columnKey, update.newValue);
      }
    }, 300); // 300ms debounce delay
  }, [updateStringField]);

  // Handle cell updates - use useCallback to prevent unnecessary re-renders
  const handleCellUpdate = useCallback(async (
    rowData: any,
    columnKey: string,
    newValue: string
  ) => {
    // Check if this is a source text edit and if there are existing translations
    if (columnKey === "sourceText" && rowData.originalTranslations) {
      const translations = rowData.originalTranslations.filter(
        (trans: any) =>
          trans &&
          trans.translated_text &&
          trans.translated_text.trim().length > 0
      );

      if (translations.length > 0) {
        // Show translation warning dialog
        setTranslationWarning({
          open: true,
          rowData,
          columnKey,
          newValue,
          translationCount: translations.length,
        });
        return; // Don't proceed with update yet
      }
    }

    // Use debounced update for better performance
    debouncedUpdate(rowData, columnKey, newValue);
  }, [debouncedUpdate]);

  // Handle language cell updates (translations) - use useCallback to prevent unnecessary re-renders
  const handleLanguageCellUpdate = useCallback(async (
    rowData: any,
    languageCode: string,
    newValue: string
  ) => {
    // Use debounced update for better performance
    // Map display field name back to API field name
    debouncedUpdate(rowData, `translations.${languageCode}.translated_text`, newValue);
  }, [debouncedUpdate]);

  // Handle translation warning dialog actions - use useCallback to prevent unnecessary re-renders
  const handleTranslationWarningConfirm = useCallback(async (
    action: "remove" | "unapprove"
  ) => {
    if (!translationWarning) return;

    const { rowData, columnKey, newValue } = translationWarning;

    // First update the source text
    await updateStringField(rowData, columnKey, newValue);

    // Then handle translations based on user choice
    const translations = rowData.originalTranslations.filter(
      (trans: any) =>
        trans &&
        trans.translated_text &&
        trans.translated_text.trim().length > 0
    );

    if (translations.length > 0) {
      for (const translation of translations) {
        const langCode = translation.language_code;
        if (langCode) {
          if (action === "remove") {
            // Remove translation text (set to empty)
            await updateStringField(
              rowData,
              `translations.${langCode}.translated_text`,
              ""
            );
          } else if (action === "unapprove") {
            // Unapprove translation (set approved to false)
            await updateStringField(
              rowData,
              `translations.${langCode}.approved`,
              "false"
            );
          }
        }
      }
    }

    // Close the dialog
    setTranslationWarning(null);
  }, [translationWarning, updateStringField]);

  const handleTranslationWarningCancel = useCallback(() => {
    setTranslationWarning(null);
  }, []);

  // Handle delete button click - use useCallback to prevent unnecessary re-renders
  const handleDeleteClick = useCallback(() => {
    if (selectedRows.size === 0) return;

    setDeleteConfirmation({
      open: true,
      selectedIds: Array.from(selectedRows),
      count: selectedRows.size,
    });
  }, [selectedRows]);

  // Function to delete a string item from the server
  const deleteStringItem = useCallback(async (id: string) => {
    try {
      if (!gameId) {
        throw new Error("No game ID available");
      }

      // Use the store function which handles glossary updates automatically
      await deleteString(id, gameId);

      return { success: true };
    } catch (error) {
      console.error("Error deleting string:", error);
      throw error;
    }
  }, [gameId]);

  // Handle delete confirmation - use useCallback to prevent unnecessary re-renders
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmation) return;

    const { selectedIds } = deleteConfirmation;

    try {
      // Delete each selected string item
      for (const id of selectedIds) {
        await deleteStringItem(id);
      }

      // Clear selection and close dialog
      setSelectedRows(new Set());
      setSelectAll(false);
      setDeleteConfirmation(null);

      // The StringsDataProvider will automatically update the data via store subscription
    } catch (error) {
      console.error("Failed to delete strings:", error);
      // You might want to show an error toast here
    }
  }, [deleteConfirmation, deleteStringItem]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  // Handle expandable cell editor
  const handleCellEditStart = useCallback((
    cellRect: DOMRect,
    value: string,
    cellWidth: number,
    maxWidth: number,
    rowData: any,
    columnKey: string
  ) => {
    setExpandableEditor({
      isVisible: true,
      cellRect,
      value,
      cellWidth,
      maxWidth,
      rowData,
      columnKey,
    });
  }, []);

  const handleExpandableEditorSave = useCallback(async () => {
    if (!expandableEditor) return;

    const { value, rowData, columnKey } = expandableEditor;
    
    console.log('💾 TMSTableWidget: handleExpandableEditorSave called', {
      rowId: rowData?.id,
      columnKey,
      value,
      isEmpty: !value || value.trim() === '',
      timestamp: new Date().toISOString()
    });
    
    // Only save if there's actual content for translation fields
    if (columnKey.includes('translations.') && columnKey.includes('.text') && (!value || value.trim() === '')) {
      console.log('🚫 TMSTableWidget: Preventing empty translation save in editor', {
        columnKey,
        value
      });
      // Don't save empty translations - just close the editor
      setExpandableEditor(null);
      return;
    }
    
    // Use the same update logic as the regular cell updates
    debouncedUpdate(rowData, columnKey, value);
    
    // Close the editor
    setExpandableEditor(null);
  }, [expandableEditor, debouncedUpdate]);

  const handleExpandableEditorCancel = useCallback(() => {
    setExpandableEditor(null);
  }, []);

  const handleExpandableEditorChange = useCallback((newValue: string) => {
    if (!expandableEditor) return;
    
    setExpandableEditor(prev => prev ? {
      ...prev,
      value: newValue
    } : null);
  }, [expandableEditor]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Scroll synchronization between fixed and scrollable sections
  useEffect(() => {
    const fixedEl = fixedScrollRef.current;
    const scrollableEl = scrollableScrollRef.current;

    if (!fixedEl || !scrollableEl) return;

    let isSyncing = false;

    // Sync from scrollable (right) to fixed (left)
    const handleScrollableScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      fixedEl.scrollTop = scrollableEl.scrollTop;
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    // Sync from fixed (left) to scrollable (right)
    const handleFixedScroll = () => {
      if (isSyncing) return;
      isSyncing = true;
      scrollableEl.scrollTop = fixedEl.scrollTop;
      requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    scrollableEl.addEventListener('scroll', handleScrollableScroll, { passive: true });
    fixedEl.addEventListener('scroll', handleFixedScroll, { passive: true });

    return () => {
      scrollableEl.removeEventListener('scroll', handleScrollableScroll);
      fixedEl.removeEventListener('scroll', handleFixedScroll);
    };
  }, []);

  // Render source column data
  const renderSourceData = (item: any, column: (typeof sourceColumns)[0]) => {
    switch (column.key) {
      case "checkbox":
        return (
          <TMSTableCell
            key={column.key}
            content=""
            type="checkbox"
            isSelected={selectedRows.has(item.id)}
            onSelectionChange={(checked) =>
              handleRowSelection(item.id, checked)
            }
            width={column.width}
            maxWidth={column.width}
            className="w-12"
            noWrapper={true}
          />
        );

      case "status":
        return (
          <TMSTableCell
            key={column.key}
            content={item.status}
            type="badge"
            status={item.status}
            width={column.width}
            maxWidth={column.width}
            noWrapper={true}
          />
        );

      case "sourceText":
        return (
          <TMSTableCell
            key={column.key}
            content={item[column.key]}
            type="text"
            title={item[column.key]}
            width={column.width}
            maxWidth={column.width}
            languageCode={item.languageCode}
            isEditable={true}
            onCellUpdate={(newValue) =>
              handleCellUpdate(item, column.key, newValue)
            }
            onCellEditStart={(cellRect, value, cellWidth, maxWidth) =>
              handleCellEditStart(cellRect, value, cellWidth, maxWidth, item, column.key)
            }
            rowData={item}
            columnKey={column.key}
            noWrapper={true}
          />
        );

      case "abbrev":
        return (
          <TMSTableCell
            key={column.key}
            content={item[column.key]}
            type="text"
            title={item[column.key]}
            width={column.width}
            maxWidth={column.width}
            languageCode={item.languageCode}
            isEditable={true}
            onCellUpdate={(newValue) =>
              handleCellUpdate(item, column.key, newValue)
            }
            onCellEditStart={(cellRect, value, cellWidth, maxWidth) =>
              handleCellEditStart(cellRect, value, cellWidth, maxWidth, item, column.key)
            }
            rowData={item}
            columnKey={column.key}
            noWrapper={true}
          />
        );

      case "context": {
        // Parse context to show shortened version (type + document name)
        // Full context shown on hover via title
        const fullContext = item.context || "";
        let shortContext = "";
        
        if (fullContext) {
          // Try to extract type and document name from context
          // Common formats: "Type > Document > Section" or "Type: Document" or "Type - Document"
          const separators = [" > ", " - ", ": ", "/"];
          let parts: string[] = [];
          
          for (const sep of separators) {
            if (fullContext.includes(sep)) {
              parts = fullContext.split(sep);
              break;
            }
          }
          
          if (parts.length >= 2) {
            // Show type and document name (first two parts)
            shortContext = `${parts[0]}: ${parts[1]}`;
          } else {
            // No separator found, show as-is but truncated
            shortContext = fullContext.length > 25 
              ? fullContext.substring(0, 25) + "..." 
              : fullContext;
          }
        }
        
        return (
          <TMSTableCell
            key={column.key}
            content={shortContext}
            type="text"
            title={fullContext || "No context"}
            width={column.width}
            maxWidth={column.width}
            isEditable={false}
            noWrapper={true}
          />
        );
      }

      default:
        return (
          <TMSTableCell
            key={column.key}
            content={item[column.key]}
            type="text"
            title={item[column.key]}
            width={column.width}
            maxWidth={column.width}
            languageCode={item.languageCode}
            noWrapper={true}
          />
        );
    }
  };

  // Render language column data
  const renderLanguageData = (
    item: any,
    lang: any,
    isRightmost = false,
    columnIndex: number
  ) => {
    // lang.code is now a string directly
    const langCode = lang.code;
    const translation = item.translations[langCode as keyof typeof item.translations];

    // Always render as editable text cell, with empty content if no translation
    return (
      <TMSTableCell
        key={langCode}
        content={translation?.text || ""}
        type="text"
        maxWidth={
          calculateLanguageColumnWidths[columnIndex] || MAX_COLUMN_WIDTH
        }
        width={calculateLanguageColumnWidths[columnIndex] || MAX_COLUMN_WIDTH}
        showLanguageFlag={false}
        languageCode={langCode}
        isRightmost={isRightmost}
        isEditable={true}
        onCellUpdate={(newValue) =>
          handleLanguageCellUpdate(item, langCode, newValue)
        }
        onCellEditStart={(cellRect, value, cellWidth, maxWidth) =>
          handleCellEditStart(cellRect, value, cellWidth, maxWidth, item, `translations.${langCode}.text`)
        }
        rowData={item}
        columnKey={`translations.${langCode}.text`}
        isEmpty={!translation?.text}
        noWrapper={true}
      />
    );
  };

  return (
    <CardBase
      className="h-full flex flex-col min-h-0 overflow-hidden"
      shadowType={shadowType}
      shadowColor={shadowColor}
      backgroundColor={backgroundColor}
      border={false}
    >
      <div className="flex-1 flex flex-col min-h-0 w-full p-2">
        {/* Filter Bar */}
        <UnifiedTableFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          availableFilters={filterConfigs}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          actions={{
            onDownload: handleDownload,
            onExport: handleExport,
            onComments: handleComments,
            onDelete: handleDeleteClick,
            hasSelectedRows: selectedRows.size > 0
          }}
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">{t('platform.cards.widgets.loading_strings_data')}</div>
          </div>
        )}

        {/* Table container with separated fixed and scrollable sections */}
        <div className="w-full h-full border border-border rounded-sm flex flex-col overflow-hidden">
          {/* Container for horizontal layout: fixed columns + scrollable columns */}
          <div className="flex flex-1 overflow-hidden">
            {/* Fixed source columns section */}
            <div 
              ref={fixedScrollRef}
              className="flex-shrink-0 border-r-2 border-border [&::-webkit-scrollbar]:hidden" 
              style={{ 
                width: `${sourceColumns.reduce((sum, col) => sum + col.width, 0)}px`,
                overflowY: 'scroll',
                overflowX: 'hidden',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
              }}
            >
              <table
                className="table-fixed"
                style={{
                  borderCollapse: "collapse",
                  borderSpacing: 0,
                  lineHeight: 1,
                  fontSize: "14px",
                  width: `${sourceColumns.reduce((sum, col) => sum + col.width, 0)}px`,
                }}
              >
                <colgroup>
                  {sourceColumns.map((column, index) => (
                    <col
                      key={index}
                      style={{
                        width: `${column.width}px`,
                      }}
                    />
                  ))}
                </colgroup>

                {/* Fixed columns header */}
                <thead className="sticky top-0 z-50">
                  <tr>
                    {sourceColumns.map((column, index) => (
                      <td
                        key={column.key}
                        className="text-center align-middle border-b-2 border-border"
                        style={{
                          width: `${sourceColumns[index].width}px`,
                        }}
                      >
                        {index === 0 ? (
                          <TMSTableCell
                            content=""
                            type="header-checkbox"
                            isHeader={true}
                            isSelected={selectAll}
                            onSelectionChange={handleSelectAll}
                            width={sourceColumns[0].width}
                            maxWidth={sourceColumns[0].width}
                            className="flex items-center justify-center"
                            noWrapper={true}
                          />
                        ) : (
                          <TMSTableCell
                            key={column.key}
                            content={column.label}
                            type="text"
                            isHeader={true}
                            width={column.width}
                            maxWidth={column.width}
                            className="w-12 flex items-center justify-center"
                            noWrapper={true}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                </thead>

                {/* Fixed columns body */}
                <tbody style={{ lineHeight: 1 }}>
                  {displayData.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-muted ${
                        item.isEmpty ? "bg-muted/50" : ""
                      }`}
                      style={{ lineHeight: 1, height: "80px" }}
                    >
                      {sourceColumns.map((column, index) => (
                        <td
                          key={column.key}
                          style={{
                            width: `${sourceColumns[index].width}px`,
                          }}
                        >
                          {renderSourceData(item, column)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Scrollable language columns section */}
            <div 
              ref={scrollableScrollRef}
              className="flex-1 overflow-auto"
            >
              <table
                className="table-fixed"
                style={{
                  borderCollapse: "collapse",
                  borderSpacing: 0,
                  lineHeight: 1,
                  fontSize: "14px",
                  width: `${calculateLanguageColumnWidths.reduce((sum, width) => sum + width, 0)}px`,
                }}
              >
                <colgroup>
                  {visibleLanguagesList.map((lang, index) => (
                    <col
                      key={lang.code}
                      style={{
                        width: `${
                          calculateLanguageColumnWidths[index] || MAX_COLUMN_WIDTH
                        }px`,
                      }}
                    />
                  ))}
                  {remainingLanguages.map((lang, index) => (
                    <col
                      key={lang.code}
                      style={{
                        width: `${
                          calculateLanguageColumnWidths[
                            visibleLanguagesList.length + index
                          ] || MAX_COLUMN_WIDTH
                        }px`,
                      }}
                    />
                  ))}
                </colgroup>

                {/* Language columns header */}
                <thead className="sticky top-0 z-50">
                  <tr>
                    {visibleLanguagesList.map((lang, index) => (
                      <td
                        key={lang.code}
                        className="align-middle border-b-2 border-border"
                        style={{
                          width: `${
                            calculateLanguageColumnWidths[index] || MAX_COLUMN_WIDTH
                          }px`,
                          height: '60px',
                        }}
                      >
                        <TMSTableCell
                          key={lang.code}
                          content={lang.name}
                          type="text"
                          isHeader={true}
                          maxWidth={calculateLanguageColumnWidths[index] || MAX_COLUMN_WIDTH}
                          width={calculateLanguageColumnWidths[index] || MAX_COLUMN_WIDTH}
                          className="flex items-center justify-center"
                          showLanguageFlag={true}
                          languageCode={lang.code}
                          noWrapper={true}
                        />
                      </td>
                    ))}
                    {remainingLanguages.map((lang, index) => (
                      <td
                        key={lang.code}
                        className="align-middle border-b-2 border-border"
                        style={{
                          width: `${
                            calculateLanguageColumnWidths[
                              visibleLanguagesList.length + index
                            ] || MAX_COLUMN_WIDTH
                          }px`,
                          height: '60px',
                        }}
                      >
                        <TMSTableCell
                          key={lang.code}
                          content={lang.name}
                          type="text"
                          isHeader={true}
                          maxWidth={calculateLanguageColumnWidths[visibleLanguagesList.length + index] || MAX_COLUMN_WIDTH}
                          width={calculateLanguageColumnWidths[visibleLanguagesList.length + index] || MAX_COLUMN_WIDTH}
                          className="text-muted-foreground flex items-center justify-center"
                          showLanguageFlag={true}
                          languageCode={lang.code}
                          isRightmost={index === remainingLanguages.length - 1}
                          noWrapper={true}
                        />
                      </td>
                    ))}
                  </tr>
                </thead>

                {/* Language columns body */}
                <tbody style={{ lineHeight: 1 }}>
                  {displayData.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-muted ${
                        item.isEmpty ? "bg-muted/50" : ""
                      }`}
                      style={{ lineHeight: 1, height: "80px" }}
                    >
                      {visibleLanguagesList.map((lang, index) => (
                        <td
                          key={lang.code}
                          style={{
                            width: `${
                              calculateLanguageColumnWidths[index] ||
                              MAX_COLUMN_WIDTH
                            }px`,
                          }}
                        >
                          {renderLanguageData(item, lang, false, index)}
                        </td>
                      ))}
                      {remainingLanguages.map((lang, index) => (
                        <td
                          key={lang.code}
                          style={{
                            width: `${
                              calculateLanguageColumnWidths[
                                visibleLanguagesList.length + index
                              ] || MAX_COLUMN_WIDTH
                            }px`,
                          }}
                        >
                          {renderLanguageData(
                            item,
                            lang,
                            index === remainingLanguages.length - 1,
                            visibleLanguagesList.length + index
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Translation Warning Dialog */}
      {translationWarning && (
        <TranslationWarningDialog
          open={translationWarning.open}
          onOpenChange={(open) => {
            if (!open) {
              setTranslationWarning(null);
            }
          }}
          onConfirm={handleTranslationWarningConfirm}
          onCancel={handleTranslationWarningCancel}
          translationCount={translationWarning.translationCount}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-destructive"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Strings
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <p className="text-foreground mb-6">
              Are you sure you want to delete{" "}
              <strong>{deleteConfirmation.count}</strong> selected string
              {deleteConfirmation.count > 1 ? "s" : ""}? This will also remove
              all translations. Glossary references will be cleaned up
              automatically.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-foreground bg-muted hover:bg-muted/80 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-destructive-foreground bg-destructive hover:bg-destructive/80 rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expandable Cell Editor - rendered at widget level for proper z-index */}
      {expandableEditor && (
        <ExpandableCellEditor
          value={expandableEditor.value}
          onChange={handleExpandableEditorChange}
          onSave={handleExpandableEditorSave}
          onCancel={handleExpandableEditorCancel}
          isVisible={expandableEditor.isVisible}
          cellRect={expandableEditor.cellRect}
          cellWidth={expandableEditor.cellWidth}
          maxWidth={expandableEditor.maxWidth}
          placeholder={t('platform.cards.widgets.enter_text')}
        />
      )}
    </CardBase>
  );
};

export default TMSTableWidget;
